import { parseWithZod } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import {
	json,
	type ActionFunctionArgs,
	type HeadersFunction,
	type LinksFunction,
	type LoaderFunctionArgs,
} from '@remix-run/node'
import {
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useFetchers,
	useLoaderData,
	useLocation,
	useSearchParams,
} from '@remix-run/react'
import { withSentry } from '@sentry/remix'
import posthog from 'posthog-js'
import { useEffect } from 'react'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { NavigationHistoryProvider } from './context/history.tsx'
import fontStyleSheetUrl from './styles/instrument-sans.css?url'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { ClientHintCheck, getHints, useHints } from './utils/client-hints.tsx'
import { getEnv } from './utils/env.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { cn, combineHeaders, getDomainUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { useRequestInfo } from './utils/request-info.ts'
import { getTheme, setTheme, type Theme } from './utils/theme.server.ts'
import { makeTimings } from './utils/timing.server.ts'

export const shouldRevalidate = () => false

export const links: LinksFunction = () => {
	return [
		// Preload CSS as a resource to avoid render blocking
		{ rel: 'preload', href: tailwindStyleSheetUrl, as: 'style' },
		{ rel: 'preload', href: fontStyleSheetUrl, as: 'style' },
		{
			rel: 'preload',
			href: '/fonts/InstrumentSans/InstrumentSans.ttf',
			as: 'font',
			type: 'font/ttf',
			crossOrigin: 'anonymous' as const,
		},
		{
			rel: 'alternate icon',
			type: 'image/png',
			href: '/favicons/favicon-32x32.png',
		},
		{ rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy

		//These should match the css preloads above to avoid css as render blocking resource
		{ rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.svg' },
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
		{ rel: 'stylesheet', href: fontStyleSheetUrl },
		// { rel: 'stylesheet', href: mapboxStyleSheetUrl },
	].filter(Boolean)
}

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings('root loader')
	// const userId = await time(() => getUserId(request), {
	// 	timings,
	// 	type: 'getUserId',
	// 	desc: 'getUserId in root',
	// })

	// const user = userId
	// 	? await time(
	// 			() =>
	// 				getUserById(userId),
	// 			{ timings, type: 'find user', desc: 'find user in root' },
	// 		)
	// 	: null
	// if (userId && !user) {
	// 	console.info('something weird happened')
	// 	// something weird happened... The user is authenticated but we can't find
	// 	// them in the database. Maybe they were deleted? Let's log them out.
	// 	await logout({ request, redirectTo: '/' })
	// }

	const honeyProps = honeypot.getInputProps()

	return json(
		{
			// user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			googleMapsApiKey: process.env.GOOGLE_MAPS_API_KEY!,
			honeyProps,
		},
		{
			headers: combineHeaders({ 'Server-Timing': timings.toString() }),
		},
	)
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
	}
	return headers
}

const ThemeFormSchema = z.object({
	theme: z.enum(['system', 'light', 'dark']),
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema: ThemeFormSchema,
	})

	invariantResponse(submission.status === 'success', 'Invalid theme received')

	const { theme } = submission.value

	const responseInit = {
		headers: { 'set-cookie': setTheme(theme) },
	}
	return json({ result: submission.reply() }, responseInit)
}

function Document({
	children,
	nonce,
	theme = 'light',
	env = {},
	googleMapsApiKey,
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string>
	googleMapsApiKey?: string
}) {
	const [searchParams] = useSearchParams()
	const screenshot = searchParams.get('screenshot')
	const { pathname } = useLocation()
	const isUtilityPage =
		/^\/(account|basket|gift-basket|login|magic-link|claim|checkout|auth|admin|resources|gift)(\/|$)/.test(
			pathname,
		) || /^\/(plot|profile)\/(edit|create-group)/.test(pathname)

	return (
		<html lang="en" className={`${theme} overscroll-none`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				{isUtilityPage ? (
					<meta name="robots" content="noindex, follow" />
				) : (
					<link rel="canonical" href={`https://erda.app${pathname}`} />
				)}
				<meta charSet="utf-8" />
				<meta
					name="viewport"
					content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
				/>
				<Links />
				{/* <Suspense>
					<script
						async
						dangerouslySetInnerHTML={{
							__html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-WGMZMSNK');`,
						}}
					/>
				</Suspense> */}
			</head>
			<body
				className={cn(
					'h-[100dvh] overscroll-none bg-gradient-to-r from-gray-500 to-gray-400',
					screenshot && 'screenshot h-[1080px]',
				)}
			>
				<noscript>
					{/* <iframe
						title="Google Tag Manager"
						src="https://www.googletagmanager.com/ns.html?id=GTM-WGMZMSNK"
						height="0"
						width="0"
						style={{ display: 'none', visibility: 'hidden' }}
					></iframe> */}
				</noscript>
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
				{googleMapsApiKey && (
					<script
						nonce={nonce}
						dangerouslySetInnerHTML={{
							__html: `
								(g=>{var h,a,k,p="The Google Maps JavaScript API",c="google",l="importLibrary",q="__ib__",m=document,b=window;b=b[c]||(b[c]={});var d=b.maps||(b.maps={}),r=new Set,e=new URLSearchParams,u=()=>h||(h=new Promise(async(f,n)=>{await (a=m.createElement("script"));e.set("libraries",[...r]+"");for(k in g)e.set(k.replace(/[A-Z]/g,t=>"_"+t[0].toLowerCase()),g[k]);e.set("callback",c+".maps."+q);a.src="https://maps."+c+"apis.com/maps/api/js?"+e;d[q]=f;a.onerror=()=>h=n(Error(p+" could not load."));a.nonce=m.querySelector("script[nonce]")?.nonce||"";m.head.append(a)}));d[l]?console.warn(p+" only loads once. Unified libraries usage is recommended."):d[l]=(f,...n)=>r.add(f)&&u().then(()=>d[l](f,...n))})({
									key: "${googleMapsApiKey}",
									v: "weekly"
								});
							`,
						}}
					/>
				)}
			</body>
		</html>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const nonce = useNonce()
	// const user = useOptionalUser()
	// const theme = useTheme()
	const location = useLocation()

	useEffect(() => {
		posthog.capture('$pageview', {
			$set_once: { initial_url: location },
		})
	}, [location])

	return (
		<Document
			nonce={nonce}
			env={data.ENV}
			googleMapsApiKey={data.googleMapsApiKey}
		>
			<Outlet />
			{/* <ThemeSwitch userPreference={data.requestInfo.userPrefs.theme} /> */}
		</Document>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<NavigationHistoryProvider>
				<App />
			</NavigationHistoryProvider>
		</HoneypotProvider>
	)
}

// export default withSentry(AppWithProviders)
export default withSentry(AppWithProviders)

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useTheme() {
	const hints = useHints()
	const requestInfo = useRequestInfo()
	const optimisticMode = useOptimisticThemeMode()
	if (optimisticMode) {
		return optimisticMode === 'system' ? hints.theme : optimisticMode
	}
	return requestInfo.userPrefs.theme ?? hints.theme
}

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to.
 */
export function useOptimisticThemeMode() {
	const fetchers = useFetchers()
	const themeFetcher = fetchers.find(f => f.formAction === '/')

	if (themeFetcher && themeFetcher.formData) {
		const submission = parseWithZod(themeFetcher.formData, {
			schema: ThemeFormSchema,
		})

		if (submission.status === 'success') {
			return submission.value.theme
		}
	}
}

// function ThemeSwitch({ userPreference }: { userPreference?: Theme | null }) {
// 	const fetcher = useFetcher<typeof action>()

// 	const [form] = useForm({
// 		id: 'theme-switch',
// 		lastResult: fetcher.data?.result,
// 	})

// 	const optimisticMode = useOptimisticThemeMode()
// 	const mode = optimisticMode ?? userPreference ?? 'system'
// 	const nextMode =
// 		mode === 'system' ? 'light' : mode === 'light' ? 'dark' : 'system'
// 	const modeLabel = {
// 		light: <span className="sr-only">Light</span>,
// 		dark: <span className="sr-only">Dark</span>,
// 		system: <span className="sr-only">System</span>,
// 	}

// 	return (
// 		<fetcher.Form method="POST" {...getFormProps(form)}>
// 			<input type="hidden" name="theme" value={nextMode} />
// 			<button
// 				type="submit"
// 				className="flex h-8 w-8 cursor-pointer items-center justify-center bg-red-500"
// 			>
// 				{modeLabel[mode]}
// 			</button>
// 		</fetcher.Form>
// 	)
// }

export function ErrorBoundary() {
	// the nonce doesn't rely on the loader so we can access that
	const nonce = useNonce()

	// NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
	// likely failed to run so we have to do the best we can.
	// We could probably do better than this (it's possible the loader did run).
	// This would require a change in Remix.

	// Just make sure your root route never errors out and you'll always be able
	// to give the user a better UX.

	return (
		<Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>
	)
}
